import axiosInstance from "../utils/axiosInstance";
import { URLS } from "../utils/baseUrls";
import { AgronomistInvoiceDto } from "../dtos/AgronomistInvoiceDto";

// Request bodies are camelCase (the interceptor converts them to snake_case);
// GET params are not converted, so they are written in snake_case.

// The API answers refusals with a 4xx/5xx body; callers get it normalized.
const withError = async (request) => {
  try {
    return await request();
  } catch (error) {
    throw Object.assign(error, {
      invoicing: AgronomistInvoiceDto.errorFromApi(error),
    });
  }
};

const fetchStatus = async () => {
  const res = await axiosInstance.get(`${URLS.agronomistInvoicing}/status/`);
  return res.data;
};

const fetchEntersoftAccount = async () => {
  const res = await axiosInstance.get(
    `${URLS.agronomistInvoicing}/entersoft-account/`
  );
  return res.data;
};

const connectEntersoft = ({ s1code, token }) =>
  withError(async () => {
    const res = await axiosInstance.put(
      `${URLS.agronomistInvoicing}/entersoft-account/`,
      { s1code, token }
    );
    return res.data;
  });

const disconnectEntersoft = async () => {
  await axiosInstance.delete(`${URLS.agronomistInvoicing}/entersoft-account/`);
};

const checkFarmer = ({ taxNumber, unitPrice, year }) =>
  withError(async () => {
    const res = await axiosInstance.post(
      `${URLS.agronomistInvoicing}/farmers/check/`,
      { taxNumber, unitPrice: unitPrice || undefined, year }
    );
    return res.data;
  });

const createInvoice = (invoice) =>
  withError(async () => {
    const res = await axiosInstance.post(
      `${URLS.agronomistInvoicing}/invoices/`,
      invoice
    );
    return AgronomistInvoiceDto.fromApi(res.data);
  });

const issueDrafts = () =>
  withError(async () => {
    const res = await axiosInstance.post(
      `${URLS.agronomistInvoicing}/invoices/issue/`,
      {}
    );
    return res.data;
  });

const createInvoiceBatch = (batch) =>
  withError(async () => {
    const res = await axiosInstance.post(
      `${URLS.agronomistInvoicing}/invoices/`,
      batch
    );
    return AgronomistInvoiceDto.batchFromApi(res.data);
  });

const fetchInvoiceBatch = async (id) => {
  const res = await axiosInstance.get(
    `${URLS.agronomistInvoicing}/bulk/${id}/`
  );
  return AgronomistInvoiceDto.batchFromApi(res.data);
};

const resumeInvoiceBatch = (id) =>
  withError(async () => {
    const res = await axiosInstance.post(
      `${URLS.agronomistInvoicing}/bulk/${id}/resume/`
    );
    return AgronomistInvoiceDto.batchFromApi(res.data);
  });

const fetchInvoice = async (id) => {
  const res = await axiosInstance.get(
    `${URLS.agronomistInvoicing}/invoices/${id}/`
  );
  return AgronomistInvoiceDto.fromApi(res.data);
};

const fetchInvoices = async ({ status, farmerTaxNumber, year } = {}) => {
  const res = await axiosInstance.get(`${URLS.agronomistInvoicing}/invoices/`, {
    params: {
      status: status || undefined,
      farmer_tax_number: farmerTaxNumber || undefined,
      year: year || undefined,
    },
  });
  return AgronomistInvoiceDto.listFromApi(res.data);
};

const updateInvoice = ({ id, ...changes }) =>
  withError(async () => {
    const res = await axiosInstance.patch(
      `${URLS.agronomistInvoicing}/invoices/${id}/`,
      changes
    );
    return AgronomistInvoiceDto.fromApi(res.data);
  });

const deleteInvoice = (id) =>
  withError(async () => {
    await axiosInstance.delete(`${URLS.agronomistInvoicing}/invoices/${id}/`);
  });

const retryInvoice = async (id) => {
  const res = await axiosInstance.post(
    `${URLS.agronomistInvoicing}/invoices/${id}/retry/`
  );
  return AgronomistInvoiceDto.fromApi(res.data);
};

export const agronomistInvoicingApi = {
  fetchStatus,
  fetchEntersoftAccount,
  connectEntersoft,
  disconnectEntersoft,
  checkFarmer,
  createInvoice,
  issueDrafts,
  createInvoiceBatch,
  fetchInvoiceBatch,
  resumeInvoiceBatch,
  fetchInvoice,
  fetchInvoices,
  updateInvoice,
  deleteInvoice,
  retryInvoice,
};
